/**
 * The sysadmin console.
 *
 * Visibly a different application from the one customers use: dark chrome, no
 * hospital branding, its own navigation. That is deliberate. Somebody who can
 * see every customer's data should never be in any doubt about which mode they
 * are in, and a console that looks like the customer app invites exactly that
 * mistake.
 */

import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { Spinner } from '../../app/Spinner';
import { type FormEvent, useId, useState } from 'react';
import own from './Console.module.css';
import form from '../../shared/Form.module.css';
import { requestSignInLink } from '../../auth/signInLink';
import { ResendLink } from '../../auth/ResendLink';
import { isCallFailure } from '../../lib/callable';
import { EMAIL_MAX } from '../../types/fieldLimits';
import { MfaGate } from './MfaGate';
import { ThemeChoice } from '../../shared/ThemeChoice';
import { Wordmark } from '../../shared/Wordmark';

const CONSOLE_NAV = [
  { to: '/console/orgs', label: 'Organizations' },
  { to: '/console/requests', label: 'Demo requests' },
  { to: '/console/sysadmins', label: 'System administrators' },
  { to: '/console/settings', label: 'System email' },
  /**
   * Operations. The console bar is one flat row, so this is the section and
   * the runbook is its landing page, with the documents as sub-navigation
   * inside it. A second level in this bar would be a redesign of the console
   * for one item.
   *
   * Every item here is inside the shell below, which has already refused
   * anybody who is not a platform system administrator. The item cannot be
   * shown to somebody the route would turn away, because the same check
   * decides both.
   */
  { to: '/console/operations/runbook', label: 'Operations' },
];

function SignIn() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [invalid, setInvalid] = useState(false);
  const id = useId();

  // The same door staff use: a one-time link by email. Google Authenticator
  // is asked for after it (MfaGate). No Google account, no password.
  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value || !value.includes('@')) { setInvalid(true); return; }
    setInvalid(false);
    setStatus('sending');
    try {
      const result = await requestSignInLink(value, { console: true });
      setMessage(result.message);
      setStatus('sent');
    } catch (err) {
      setMessage(isCallFailure(err) ? err.message : 'Something went wrong. Please try again.');
      setStatus('error');
    }
  }

  return (
    <main className={own.signInPage}>
      <div className={own.signInCard}>
        <div className={own.brand}>
          <Wordmark className={own.brandMark} />
          <span className={own.brandSub}>Console</span>
        </div>
        <h1 className={own.signInTitle}>System administration</h1>
        <p className={own.signInBody}>
          This console reaches every organization on the system. Enter your address and we
          will email you a sign-in link; Google Authenticator is asked for after it.
        </p>

        {status === 'sent' ? (
          <>
            <div className={own.signInBody} role="status">{message}</div>
            <ResendLink
              email={email.trim()}
              send={() => requestSignInLink(email.trim(), { console: true })}
              buttonClassName={own.plainBtn ?? ''}
              className={own.signInBody}
            />
          </>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            {status === 'error' && <div className={own.error} role="alert">{message}</div>}
            <label className={form.label} htmlFor={id}>Email</label>
            <input
              id={id}
              className={form.input}
              type="email"
              autoComplete="email"
              maxLength={EMAIL_MAX}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={invalid || undefined}
              aria-describedby={invalid ? `${id}-err` : undefined}
              disabled={status === 'sending'}
              autoFocus
            />
            {invalid && <p className={form.error} id={`${id}-err`}>Enter your email address.</p>}
            <div style={{ marginTop: 'var(--sp-4)' }}>
              <button type="submit" className={own.googleBtn} disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}

function NotSysadmin({ email }: { email: string | null }) {
  const { signOut } = useAuth();
  return (
    <main className={own.signInPage}>
      <div className={own.signInCard}>
        <h1 className={own.signInTitle}>Not a system administrator</h1>
        <p className={own.signInBody}>
          {email ?? 'That account'} is signed in, but it does not hold system
          administrator access. If you are looking for your own organization's
          policies, use the main application instead.
        </p>
        <button type="button" className={own.plainBtn} onClick={signOut}>
          Sign out
        </button>
      </div>
    </main>
  );
}

export function ConsoleShell() {
  const { loading, session } = useAuth();

  if (loading) return <Spinner label="Checking your session" />;
  if (!session) return <SignIn />;

  // The claim is what decides, and the backend re-checks it on every call.
  // This only avoids rendering a console whose every request would be refused.
  // A platform system administrator only. An organization's own System
  // Admin also carries the sysadmin role, for their organization; the
  // console is not theirs.
  if (!session.platformSysAdmin) {
    return <NotSysadmin email={session.email} />;
  }

  // Then the second factor. The backend refuses every sysadmin call without
  // it; this keeps a console from rendering that could do nothing.
  return (
    <MfaGate>
      <Shell />
    </MfaGate>
  );
}

function Shell() {
  const { session, signOut } = useAuth();
  if (!session) return null;
  return (
    <div className={own.shell}>
      <header className={own.bar}>
        <div className={own.brand}>
          <Wordmark onChrome className={own.brandMark} />
          <span className={own.brandSub}>Console</span>
        </div>

        <nav aria-label="Console">
          <ul className={own.nav}>
            {CONSOLE_NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    isActive ? `${own.navLink} ${own.navLinkOn}` : own.navLink
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className={own.who}>
          <ThemeChoice tone="chrome" />
          <span>{session.email}</span>
          <button type="button" className={own.signOut} onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      <main id="main" tabIndex={-1} className={own.body}>
        <Outlet />
      </main>
    </div>
  );
}
